import Icon, { IconType } from 'components/Icon';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
	setForceTransientSidebarClosed,
	setForceTransientSidebarReleasePending,
	setSidebarHovered,
	setSidebarVisible,
} from 'redux/mode/actions';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import { useTheme } from 'styled-components';
import * as S from './styles';

interface SidebarToggleProps {
	isFloating?: boolean;
}

const SidebarToggle = ({ isFloating = false }: SidebarToggleProps) => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const {
		sidebarVisible,
		sidebarDocked,
		forceTransientSidebarClosed,
		forceTransientSidebarReleasePending,
		isMobile,
	} = useSelector((r: RootReducer) => {
		return {
			sidebarVisible: r.mode.sidebarVisible,
			sidebarDocked: r.mode.sidebarDocked,
			forceTransientSidebarClosed: r.mode.forceTransientSidebarClosed,
			forceTransientSidebarReleasePending: r.mode.forceTransientSidebarReleasePending,
			isMobile: selectIsMobile(r.mode),
		};
	}, shallowEqual);

	const onClick = () => {
		if (isMobile) {
			dispatch(setSidebarVisible(!sidebarVisible));
		}
	};

	const onMouseEnter = () => {
		if (isMobile || sidebarDocked) return;
		// Hovering the moon is the explicit "open sidebar" gesture. If a pending release
		// is outstanding (modal just closed and the sidebar's bounding box hasn't been
		// exited), this gesture is the user's intent to bring it back — drop the lock.
		if (forceTransientSidebarReleasePending) {
			dispatch(setForceTransientSidebarReleasePending(false));
			dispatch(setForceTransientSidebarClosed(false));
			dispatch(setSidebarHovered(true));
			return;
		}
		// While the flag is genuinely held (modal still open), block — sidebar should not
		// appear while the modal owns the screen.
		if (forceTransientSidebarClosed) return;
		dispatch(setSidebarHovered(true));
	};

	const onMouseLeave = () => {
		if (!isMobile && !sidebarDocked) {
			dispatch(setSidebarHovered(false));
		}
	};

	return (
		<S.Base
			data-testid="sidebar-toggle"
			isMobile={isMobile}
			isFloating={isFloating}
			onClick={isMobile ? onClick : undefined}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			<Icon
				type={IconType.MOONCHILD}
				width={isMobile ? 28 : 22}
				height={isMobile ? 28 : 22}
				color={theme.blue[900]}
			/>
		</S.Base>
	);
};

export default SidebarToggle;
